import { escapeHtml } from '../util/escapeHtml.js';
/**
 * ItemInfoPanel — full-screen modal overlay that opens when a player clicks
 * an item's name in their inventory (or anywhere else that calls
 * `attachItemInfoToggle`).
 *
 * Lazy-fetches /items/info/:slug the first time a slug is opened and caches
 * the response in-memory so subsequent opens are instant.
 *
 * Design (2026-05 refactor): previously the panel expanded inline beneath
 * each card, which broke layout when inventory grew large. Now it renders as
 * a singleton centered modal with a click-outside / Escape / X-button close,
 * so the surrounding inventory grid is never disturbed.
 *
 * Usage:
 *   • Call `attachItemInfoToggle(nameEl, slug)` for each inventory item name.
 *   • The single global modal is created on first use and reused thereafter.
 */

import { api, HttpError } from '../api/client.js';
import { formatCrowns } from '../util/formatBig.js';
import { navigate } from '../router.js';
import type { ItemInfoResponse } from '@rex-mortis/shared';

const HOUSE_LABEL: Record<string, string> = {
  forge: 'The Forge',
  apothecary: 'The Apothecary',
  cookfire: 'The Cookfire',
  brewhouse: 'The Brewhouse',
};

const INFO_CACHE = new Map<string, ItemInfoResponse>();

const TIER_LABELS: Record<string, string> = {
  crude: 'Crude',
  common: 'Common',
  fine: 'Fine',
  masterwork: 'Masterwork',
  legendary: 'Legendary',
  artifact: 'Artifact',
  mythic: 'Mythic',
};

const TIER_RING: Record<string, string> = {
  crude:      'var(--c-ink-soft)',
  common:     'var(--c-rule-strong)',
  fine:       'var(--c-spirit)',
  masterwork: '#a85a1c',
  legendary:  '#b48b1c',
  artifact:   '#7a3aa2',
  mythic:     '#c84040',
};

function renderInfoBody(info: ItemInfoResponse): string {
  const tierLabel = TIER_LABELS[info.tier] ?? info.tier;
  const ring = TIER_RING[info.tier] ?? 'var(--c-rule-strong)';
  const sourcesList = info.sources.map((s) => `<li>${escapeHtml(s)}</li>`).join('');
  const usedIn = info.usedIn ?? [];
  const usedInSection = usedIn.length
    ? `
        <div class="iim-section">
          <div class="iim-section-label">Used in crafting</div>
          <ul class="iim-recipes">
            ${usedIn
              .map(
                (u) =>
                  `<li><button type="button" class="iim-recipe" data-house="${escapeHtml(u.house)}" title="Open ${escapeHtml(HOUSE_LABEL[u.house] ?? u.house)}"><span class="iim-recipe-name">${escapeHtml(u.producesName)}</span><span class="iim-recipe-house">${escapeHtml(HOUSE_LABEL[u.house] ?? u.house)}</span></button></li>`,
              )
              .join('')}
          </ul>
        </div>`
    : '';
  const loreBadge = info.loreIsBespoke ? '' : '<span class="iim-fallback-pill" title="No bespoke lore yet — showing the catalog blurb.">blurb</span>';
  const circ = info.circulation;
  const circLabel = circ.totalQty === 0
    ? 'None in circulation right now'
    : `${circ.totalQty.toLocaleString()} in circulation across ${circ.owners.toLocaleString()} ${circ.owners === 1 ? 'owner' : 'owners'}`;

  // Stat lines (only show non-null).
  const statLines: string[] = [];
  if (info.attack != null)   statLines.push(`<span class="iim-stat-pill"><strong>${info.attack}</strong> attack</span>`);
  if (info.accuracy != null) statLines.push(`<span class="iim-stat-pill"><strong>${info.accuracy}</strong> accuracy</span>`);
  if (info.defense != null)  statLines.push(`<span class="iim-stat-pill"><strong>${info.defense}</strong> defense</span>`);
  if (info.perks?.battleStatPct) statLines.push(`<span class="iim-stat-pill mythic">+${info.perks.battleStatPct}% all battle stats</span>`);
  if (info.perks?.lifestealPct)  statLines.push(`<span class="iim-stat-pill mythic">${info.perks.lifestealPct}% lifesteal</span>`);

  return `
    <button class="iim-close" type="button" aria-label="Close" title="Close (Esc)">×</button>
    <header class="iim-header">
      <h2 class="iim-name">${escapeHtml(info.name)}</h2>
      <div class="iim-header-meta">
        <span class="iim-tier" style="border-color: ${ring}; color: ${ring}">${escapeHtml(tierLabel)}</span>
        ${loreBadge}
        <span class="iim-value">${formatCrowns(info.baseValue)} crowns</span>
      </div>
    </header>

    <div class="iim-grid">
      <div class="iim-art">
        <img src="/img/item-art/${info.slug}.webp" alt="" onerror="this.style.display='none'; this.parentElement.classList.add('iim-art--missing');" />
        <div class="iim-art-placeholder">No artwork yet</div>
      </div>
      <div class="iim-body">
        <div class="iim-use">${escapeHtml(info.use)}</div>

        ${statLines.length > 0 ? `<div class="iim-stats">${statLines.join('')}</div>` : ''}

        <div class="iim-section">
          <div class="iim-section-label">Lore</div>
          <div class="iim-lore">${escapeHtml(info.lore)}</div>
        </div>

        <div class="iim-section">
          <div class="iim-section-label">Where it comes from</div>
          <ul class="iim-sources">${sourcesList}</ul>
        </div>

        ${usedInSection}

        <div class="iim-section iim-circulation">
          <div class="iim-section-label">In circulation</div>
          <div>${escapeHtml(circLabel)}</div>
        </div>
      </div>
    </div>
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// Singleton modal — created once per page-load, reused for every item click.
// ──────────────────────────────────────────────────────────────────────────────

interface ModalElements {
  backdrop: HTMLDivElement;
  modal: HTMLDivElement;
  onKey: (e: KeyboardEvent) => void;
}

let modalEls: ModalElements | null = null;

function ensureModal(): ModalElements {
  if (modalEls) return modalEls;
  ensureItemInfoStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'iim-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.addEventListener('click', closeModal);

  const modal = document.createElement('div');
  modal.className = 'iim-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-hidden', 'true');
  // Click inside the modal should NOT close it.
  modal.addEventListener('click', (e) => e.stopPropagation());

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && modal.classList.contains('open')) {
      closeModal();
    }
  };
  document.addEventListener('keydown', onKey);

  document.body.appendChild(backdrop);
  document.body.appendChild(modal);

  modalEls = { backdrop, modal, onKey };
  return modalEls;
}

function closeModal(): void {
  if (!modalEls) return;
  modalEls.backdrop.classList.remove('open');
  modalEls.modal.classList.remove('open');
  modalEls.backdrop.setAttribute('aria-hidden', 'true');
  modalEls.modal.setAttribute('aria-hidden', 'true');
  // Restore page scroll.
  document.body.style.overflow = '';
}

async function openModal(slug: string): Promise<void> {
  const els = ensureModal();
  els.backdrop.classList.add('open');
  els.modal.classList.add('open');
  els.backdrop.setAttribute('aria-hidden', 'false');
  els.modal.setAttribute('aria-hidden', 'false');
  // Prevent body scroll while modal is open.
  document.body.style.overflow = 'hidden';
  // Show a loading state while we fetch.
  els.modal.innerHTML = '<div class="iim-loading">Reading the inventory ledger…</div>';

  // Load (or use cache).
  let info = INFO_CACHE.get(slug);
  if (!info) {
    try {
      info = await api.getItemInfo(slug);
      INFO_CACHE.set(slug, info);
    } catch (err) {
      els.modal.innerHTML = `
        <button class="iim-close" type="button" aria-label="Close" title="Close (Esc)">×</button>
        <div class="iim-error">${escapeHtml(err instanceof HttpError ? err.message : 'Could not load item info.')}</div>
      `;
      wireClose(els.modal);
      return;
    }
  }

  els.modal.innerHTML = renderInfoBody(info);
  wireClose(els.modal);
  // "Used in crafting" links → close the modal and jump to that crafting house.
  els.modal.querySelectorAll<HTMLButtonElement>('.iim-recipe').forEach((b) => {
    b.addEventListener('click', () => {
      const house = b.dataset.house;
      if (!house) return;
      closeModal();
      navigate(`/${house}`);
    });
  });
}

function wireClose(modal: HTMLElement): void {
  const closeBtn = modal.querySelector<HTMLButtonElement>('.iim-close');
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
}

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────

export function ensureItemInfoStyles(): void {
  if (document.getElementById('iim-styles')) return;
  const style = document.createElement('style');
  style.id = 'iim-styles';
  style.textContent = STYLES;
  document.head.appendChild(style);
}

/**
 * Make `nameEl` a click-target that opens the global item-info modal.
 *
 * The `containerEl` option from the old API is accepted but ignored — the
 * modal now lives at the document-body level, not inside any card. This
 * preserves call-site compatibility while fixing the layout problem.
 */
export function attachItemInfoToggle(
  nameEl: HTMLElement,
  slug: string,
  _options: { containerEl?: HTMLElement } = {},
): void {
  ensureItemInfoStyles();
  nameEl.classList.add('iim-name-toggle');
  nameEl.setAttribute('role', 'button');
  nameEl.setAttribute('tabindex', '0');

  const handler = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    void openModal(slug);
  };

  nameEl.addEventListener('click', handler);
  nameEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') handler(e);
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Styles — prefixed `.iim-` (item-info-modal) so they don't collide with the
// old in-card panel styles still present in any cached page.
// ──────────────────────────────────────────────────────────────────────────────

const STYLES = `
  .iim-name-toggle {
    cursor: pointer;
    text-decoration: none;
    border-bottom: 1px dotted var(--c-rule);
    transition: color 0.12s ease;
  }
  .iim-name-toggle:hover { color: var(--c-oxblood); }
  .iim-name-toggle::after {
    content: ' ▸';
    color: var(--c-ink-soft);
    font-size: 0.85em;
  }

  .iim-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(28, 17, 8, 0.55);
    backdrop-filter: blur(2px);
    z-index: 400;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }
  .iim-backdrop.open {
    opacity: 1;
    pointer-events: auto;
  }

  .iim-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.96);
    width: min(880px, 94vw);
    max-height: 92vh;
    overflow-y: auto;
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius-lg);
    box-shadow:
      0 0 0 1px var(--c-gold),
      0 20px 60px rgba(0, 0, 0, 0.5);
    padding: var(--s-4);
    z-index: 401;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  .iim-modal.open {
    opacity: 1;
    pointer-events: auto;
    transform: translate(-50%, -50%) scale(1);
  }

  .iim-close {
    position: absolute;
    top: var(--s-2);
    right: var(--s-2);
    width: 36px;
    height: 36px;
    border: 1px solid var(--c-rule);
    background: var(--c-parchment);
    border-radius: 50%;
    cursor: pointer;
    font-size: 1.5rem;
    line-height: 1;
    color: var(--c-ink-soft);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
  }
  .iim-close:hover {
    color: var(--c-oxblood);
    border-color: var(--c-oxblood);
    background: var(--c-parchment-dark);
  }

  .iim-loading, .iim-error {
    font-style: italic;
    color: var(--c-ink-soft);
    padding: var(--s-5);
    text-align: center;
  }
  .iim-error { color: var(--c-danger); }

  .iim-header {
    border-bottom: 1px solid var(--c-rule-soft);
    padding: 0 calc(36px + var(--s-2)) var(--s-3) 0;
    margin-bottom: var(--s-3);
  }
  .iim-name {
    margin: 0 0 var(--s-1) 0;
    font-family: var(--font-heading);
    font-size: 1.7rem;
    color: var(--c-ink);
    letter-spacing: 0.02em;
  }
  .iim-header-meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
    align-items: center;
  }
  .iim-tier {
    font-family: var(--font-display);
    font-size: 0.78rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 8px;
    border: 1px solid;
    border-radius: 3px;
    background: var(--c-parchment-dark);
  }
  .iim-fallback-pill {
    font-family: var(--font-display);
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 1px 6px;
    border-radius: 3px;
    color: var(--c-ink-soft);
    background: var(--c-parchment-dark);
    border: 1px solid var(--c-rule-soft);
  }
  .iim-value {
    margin-left: auto;
    font-family: var(--font-numeric);
    color: var(--c-gold-text);
    font-weight: 700;
    font-size: 1.02rem;
  }

  .iim-grid {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: var(--s-4);
    align-items: start;
  }
  @media (max-width: 640px) {
    .iim-grid { grid-template-columns: 1fr; }
    .iim-art { max-width: 280px; margin: 0 auto; }
  }

  .iim-art {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background: var(--c-parchment-dark);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .iim-art img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .iim-art-placeholder {
    position: absolute;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    font-family: var(--font-elegant);
    font-style: italic;
    color: var(--c-ink-soft);
    font-size: 0.95rem;
    text-align: center;
    padding: var(--s-2);
  }
  .iim-art.iim-art--missing img { display: none; }
  .iim-art.iim-art--missing .iim-art-placeholder { display: flex; }

  .iim-body {
    display: flex;
    flex-direction: column;
    gap: var(--s-3);
    min-width: 0;
  }
  .iim-use {
    font-family: var(--font-display);
    color: var(--c-ink);
    font-size: 0.98rem;
    background: rgba(180, 139, 28, 0.10);
    border-left: 3px solid var(--c-gold);
    padding: var(--s-2) var(--s-3);
    border-radius: 0 var(--radius) var(--radius) 0;
  }
  .iim-stats {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-1);
  }
  .iim-stat-pill {
    background: var(--c-parchment-dark);
    border: 1px solid var(--c-rule);
    border-radius: 999px;
    padding: 2px 12px;
    font-family: var(--font-numeric);
    font-size: 0.9rem;
    color: var(--c-ink);
  }
  .iim-stat-pill.mythic {
    border-color: #c84040;
    color: #c84040;
    font-weight: 700;
  }
  .iim-section { margin-top: var(--s-1); }
  .iim-section-label {
    font-family: var(--font-display);
    font-size: 0.74rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--c-ink-soft);
    margin-bottom: 6px;
  }
  .iim-lore {
    font-family: var(--font-elegant);
    font-style: italic;
    color: var(--c-ink);
    line-height: 1.6;
    font-size: 0.98rem;
  }
  .iim-sources {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .iim-sources li {
    background: var(--c-parchment-dark);
    border: 1px solid var(--c-rule-soft);
    border-radius: var(--radius);
    padding: 4px 12px;
    font-size: 0.94rem;
    color: var(--c-ink);
  }
  .iim-sources li::before {
    content: '↳ ';
    color: var(--c-ink-soft);
  }
  .iim-recipes {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .iim-recipe {
    width: 100%;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    background: var(--c-parchment-dark);
    border: 1px solid var(--c-rule-soft);
    border-radius: var(--radius);
    padding: 5px 12px;
    font: inherit;
    color: var(--c-ink);
    cursor: pointer;
    transition: border-color 0.12s ease, color 0.12s ease;
  }
  .iim-recipe:hover { border-color: var(--c-gold); color: var(--c-oxblood); }
  .iim-recipe-name { min-width: 0; overflow-wrap: anywhere; }
  .iim-recipe-name::before { content: '⚒ '; color: var(--c-ink-soft); }
  .iim-recipe-house {
    font-family: var(--font-display);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--c-ink-soft);
    white-space: nowrap;
  }
  .iim-circulation {
    font-family: var(--font-numeric);
    color: var(--c-ink);
  }
`;
